import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Flex, Text, Button, Grid, Card, Separator, Tooltip, Badge, Progress, Box } from "@radix-ui/themes";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { useGetAllBrands } from "hooks";
import { useProfileContext, useLocationContext } from "context";
import { useQueries } from "@tanstack/react-query";
import { attemptGetActiveLocationGuestPortalSettings } from "api/OrgApi";

const sortByName = (items = []) => [...items].sort((firstItem, secondItem) =>
  (firstItem?.name || "").localeCompare(secondItem?.name || "", undefined, { sensitivity: "base" })
);

const TruncatedText = ({ children, size, weight, color, style }) => {
  const text = typeof children === 'string' ? children : String(children);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const checkTruncation = () => {
      const container = containerRef.current;
      if (container) {
        setIsTruncated(container.scrollWidth > container.clientWidth);
      }
    };

    // Initial check
    checkTruncation();

    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);

    // Recheck if text changes
    const observer = new MutationObserver(checkTruncation);
    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, characterData: true, subtree: true });
    }

    return () => {
      window.removeEventListener('resize', checkTruncation);
      observer.disconnect();
    };
  }, [text]); // Rerun effect if text content changes

  const textElement = (
    <Box as="span" ref={containerRef} style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...style // Allow passing additional styles
      }}>
      <Text size={size} weight={weight} color={color} style={{width: "100%"}}>
        {text}
      </Text>
    </Box>
  );

  return isTruncated ? (
    <Tooltip content={text}>
      {textElement}
    </Tooltip>
  ) : textElement;
};

const GuestPortalSelector = () => {
  const navigate = useNavigate();
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();

  // Fetch all brands for this organisation using the same hook as OrganisationBrands
  const { data: brandsData, isLoading: isLoadingBrands, error: brandsError } = useGetAllBrands(userProfile.organisation_id);

  // State to hold filtered data
  const [brands, setBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);

  // Process the data when it's loaded
  useEffect(() => {
    if (brandsData && locationsData) {
      // Find the default brand
      const defaultBrandItem = brandsData.find((brand) => brand.is_default);

      // Filter out the default brand and get non-default brands
      const nonDefaultBrands = sortByName(brandsData.filter((brand) => !brand.is_default));
      setBrands(nonDefaultBrands);

      // Filter locations that use the default brand
      const defaultLocations = sortByName(
        locationsData.filter((location) => location.brand_id === defaultBrandItem?.id || location.brand_id === null)
      );
      setIndependentLocations(defaultLocations);
    }
  }, [brandsData, locationsData]);

  const locationSettingsQueries = useQueries({
    queries: independentLocations.map((location) => ({
      queryKey: ["location-guest-portal-settings", location.id],
      queryFn: () => attemptGetActiveLocationGuestPortalSettings(location.id),
      enabled: !!location.id,
    })),
  });

  // build a quick map: locationId → settings.data
  const locationSettingsMap = useMemo(() => {
    const m = {};
    locationSettingsQueries.forEach((q, i) => {
      const loc = independentLocations[i];
      if (loc && q.data) {
        m[loc.id] = q.data.data; // your API returns { data: { logo, background_image, … } }
      }
    });
    return m;
  }, [locationSettingsQueries, independentLocations]);

  // Handle brand click
  const handleBrandClick = (brand) => {
    navigate(`/portal-brand/${brand.id}/planner`, {
      state: {
        isBrand: true,
        brandId: brand.id,
        brandName: brand.name,
        brandLocationCount: brand.locations?.length || 0,
      },
    });
  };

  // Handle independent location click
  const handleLocationClick = (location) => {
    navigate(`/portal-location/${location.id}/planner`, {
      state: {
        isBrand: false,
        locationName: location.name,
        locationAddress: location.address,
      },
    });
  };

  // Simple loading state
  if (isLoadingBrands || !locationsData) {
    return (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
    );
  }

  // Simple error state
  if (brandsError) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "400px" }}>
        <Text color="red">Error loading brand information</Text>
      </Flex>
    );
  }

  function colorConversion(inputColour, opacity) {
    // Handle case when parameters are undefined
    if (!inputColour || opacity === undefined) return "rgba(0, 0, 0, 0)";

    // Convert hex to rgb
    let hex = inputColour.replace("#", "");

    // Handle both 3-char and 6-char hex
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Return rgba value with the specified opacity
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  const navigateToOrganisationLocations = () => {
    navigate("/organisation/locations");
  };

  const navigateToOrganisationLocationsWithBrandModal = () => {
    navigate("/organisation/locations", {
      state: { openEditBrands: true },
      replace: true,
    });
  };

  return (
    <Flex direction="column" style={{ alignSelf: "stretch", width: "100%", overflow: "auto" }}>
      {/* Main Content Section */}
      <Flex direction="column" gap="5" style={{ width: "100%" }}>
        {brands.length > 0 && (
          <Flex direction="column" gap="4">
            {/* Brands header */}
            <Flex style={{ justifyContent: "space-between", alignItems: "center", alignSelf: "stretch" }}>
              <Text size="3" weight="medium">Brands</Text>
              <Button size="2" variant="soft" color="gray" onClick={navigateToOrganisationLocationsWithBrandModal}>
                Edit brands
              </Button>
            </Flex>
            {/* Brands portal section */}
            <Flex direction="column" style={{ alignSelf: "stretch", width: "100%", justifyContent: "center", alignItems: "center" }}>
              <Grid columns="3" gap="4" style={{ alignSelf: "stretch", width: "100%" }}>
                {brands.map((brand) => (
                  <Card asChild key={brand.id}                    
                    style={{ padding: "0", gap: "0", height: "200px", display: "flex", flexDirection: "column", 
                      justifyContent: "center", overflow: "hidden" 
                    }}
                    onClick={() => handleBrandClick(brand)}>
                    <button>
                      <Flex
                        style={{
                          background: brand.portal_settings?.background_image
                            ? `linear-gradient(${colorConversion(brand.portal_settings?.background_overlay_colour, brand.portal_settings?.background_overlay_opacity)}, ${colorConversion(
                                brand.portal_settings?.background_overlay_colour,
                                brand.portal_settings?.background_overlay_opacity
                              )}), url(${brand.portal_settings?.background_image}) no-repeat center center / cover`
                            : `linear-gradient(${colorConversion(brand.portal_settings?.background_overlay_colour, brand.portal_settings?.background_overlay_opacity)}, ${colorConversion(
                                brand.portal_settings?.background_overlay_colour,
                                brand.portal_settings?.background_overlay_opacity
                              )}), #f6f6f8`,
                          width: "100%",
                          height: "100%",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {brand.portal_settings?.logo && (
                          <img
                            src={brand.portal_settings.logo}
                            alt="Brand Logo"
                            style={{ width: "80px", height: "80px", objectFit: "contain", borderTopLeftRadius: "var(--space-2)", borderTopRightRadius: "var(--space-2)" }}
                          />
                        )}
                      </Flex>
                      <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                      <Flex p="4" justify="between" align="center" style={{ alignSelf: "stretch" }}>
                        <Flex gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Text size="2" weight="medium" style={{ color: "var(--gray-12)" }}>
                            {brand.name}
                          </Text>
                          <Text size="1" color="gray">
                            {(brand.locations?.length || 0) === 1 ? `${brand.locations.length} location` : `${brand.locations.length} locations`}
                          </Text>
                        </Flex>
                        {brand.portal_settings?.active_plan?.is_main ? (
                          <Tooltip content="Currently active">
                            <Badge color="amber">Default</Badge>
                          </Tooltip>
                        ) : (
                          <Tooltip content={`Currently active until ${brand.portal_settings?.active_plan?.end_date}`}>
                            <Badge color="grass">{brand.portal_settings?.active_plan?.title}</Badge>
                          </Tooltip>
                        )}
                      </Flex>
                    </button>
                  </Card>
                ))}
              </Grid>
            </Flex>
          </Flex>
        )}

        {independentLocations.length > 0 && (
          <Flex direction="column" gap="4">
            { brands.length > 0 && (<Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }}/>)}
            {/* Locations header section */}
            <Flex style={{ justifyContent: "space-between", alignItems: "center", alignSelf: "stretch" }}>
              <Flex gap="2" style={{ alignItems: "center", alignSelf: "stretch", justifyContent: "flex-start" }}>
                <Text size="3" weight="medium">Locations</Text>
                <Tooltip maxWidth="202px" style={{ textAlign: "center" }} 
                  content="Independently managed locations, not assigned to any brand.">
                  <InfoCircledIcon />
                </Tooltip>
              </Flex>
              <Button size="2" variant="soft" color="gray" onClick={navigateToOrganisationLocations}>
                Edit locations
              </Button>
            </Flex>
            {/* Locations portal settings */}
            <Flex direction="column" style={{ alignSelf: "stretch", width: "100%", justifyContent: "center", alignItems: "center" }}>
              <Grid columns="3" gap="4" style={{ alignSelf: "stretch", width: "100%" }}>
                {independentLocations.map((location) => (
                  <Card asChild key={location.id}
                    style={{ padding: "0px", gap: "0", height: "200px", display: "flex", flexDirection: "column", 
                      justifyContent: "center", overflow: "hidden" 
                    }}
                    onClick={() => handleLocationClick(location)}>
                    <button>
                      <Flex
                        style={{
                          background: locationSettingsMap[location.id]?.background_image
                            ? `linear-gradient(${colorConversion(
                                locationSettingsMap[location.id]?.background_overlay_colour,
                                locationSettingsMap[location.id]?.background_overlay_opacity
                              )}, ${colorConversion(locationSettingsMap[location.id]?.background_overlay_colour, locationSettingsMap[location.id]?.background_overlay_opacity)}), url(${
                                locationSettingsMap[location.id]?.background_image
                              }) no-repeat center center / cover`
                            : `linear-gradient(${colorConversion(
                                locationSettingsMap[location.id]?.background_overlay_colour,
                                locationSettingsMap[location.id]?.background_overlay_opacity
                              )}, ${colorConversion(locationSettingsMap[location.id]?.background_overlay_colour, locationSettingsMap[location.id]?.background_overlay_opacity)}), #f6f6f8`,
                          width: "100%",
                          height: "100%",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {locationSettingsMap[location.id]?.logo && (
                          <img
                            src={locationSettingsMap[location.id]?.logo}
                            alt="Brand Logo"
                            style={{ width: "80px", height: "80px", objectFit: "contain", borderTopLeftRadius: "var(--space-2)", borderTopRightRadius: "var(--space-2)" }}
                          />
                        )}
                      </Flex>
                      <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                      <Flex p="4" justify="between" align="center" style={{ alignSelf: "stretch" }}>
                        <Flex gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", width: "75%" }}>
                          <Text size="2" weight="medium" style={{ color: "var(--gray-12)" }}>{location.name}</Text>
                          <TruncatedText size="1" color="gray">{location.address}</TruncatedText>
                        </Flex>
                        {locationSettingsMap[location.id]?.active_plan?.is_main ? (
                          <Tooltip content="Currently active">
                            <Badge color="amber">Default</Badge>
                          </Tooltip>
                        ) : (
                          <Tooltip content={`Currently active until ${locationSettingsMap[location.id]?.active_plan?.end_date}`}>
                            <Badge color="grass">{locationSettingsMap[location.id]?.active_plan?.title}</Badge>
                          </Tooltip>
                        )}
                      </Flex>
                    </button>
                  </Card>
                ))}
              </Grid>
            </Flex>
          </Flex>
        )}

        {brands.length === 0 && independentLocations.length === 0 ? (
          // No brands and no independent locations
          <Flex direction="column" gap="5" style={{ alignSelf: "stretch", height: "100%", width: "100%" }}>
            {/* No brands section */}
            <Flex direction="column" gap="4">
              {/* Brands header */}
              <Text size="3" weight="medium">Brands</Text>
              <Flex direction="column" gap="5" style={{ alignItems: "center", justifyContent: "center", width: "100%", 
                borderRadius: "var(--space-2)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
                <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: "296px" }}>
                  <Text size="3" weight="medium">No brands</Text>
                  <Text size="2" color="gray" align="center">
                    Create brands to easily manage captive portals and networks across your locations.
                  </Text>
                </Flex>
                <Button size="2" variant="soft" onClick={navigateToOrganisationLocationsWithBrandModal}>
                  Manage brands
                </Button>
              </Flex>
            </Flex>
            {/* No independent locations section */}
            <Flex direction="column" gap="4">
              <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }}/>
              <Flex gap="2" style={{ alignItems: "center", alignSelf: "stretch", justifyContent: "flex-start" }}>
                <Text size="3" weight="medium">
                  Locations
                </Text>
                <Tooltip maxWidth="202px" style={{ textAlign: "center" }} content="Independently managed locations, not assigned to any brand.">
                  <InfoCircledIcon />
                </Tooltip>
              </Flex>
              <Flex
                direction="column"
                gap="5"
                style={{ alignItems: "center", justifyContent: "center", width: "100%", borderRadius: "var(--space-2)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}
              >
                <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: "296px" }}>
                  <Text size="2" weight="medium">
                    No independent locations
                  </Text>
                  <Text size="1" color="gray" align="center">
                    Create your first location to start setting up your captive portals and networks.
                  </Text>
                </Flex>
                <Button size="2" variant="soft" onClick={navigateToOrganisationLocations}>
                  Manage locations
                </Button>
              </Flex>
            </Flex>
          </Flex>
        ) : brands.length > 0 && independentLocations.length === 0 ? (
          <Flex direction="column" gap="4">
            <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }}/>
            <Flex gap="2" style={{ alignItems: "center", alignSelf: "stretch", justifyContent: "flex-start" }}>
              <Text size="3" weight="medium">Locations</Text>
              <Tooltip maxWidth="202px" style={{ textAlign: "center" }} 
                content="Independently managed locations, not assigned to any brand.">
                <InfoCircledIcon />
              </Tooltip>
            </Flex>
            <Flex direction="column" gap="5" style={{ alignItems: "center", justifyContent: "center", width: "100%", 
              borderRadius: "var(--radius-4)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
              <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: "296px" }}>
                <Text size="3" weight="medium">No independent locations</Text>
                <Text size="2" color="gray" align="center">
                  Create your first location to start setting up your captive portals and networks.
                </Text>
              </Flex>
              <Button size="2" variant="soft" onClick={navigateToOrganisationLocations}>
                Manage locations
              </Button>
            </Flex>
          </Flex>
        ) : (
          independentLocations.length > 0 && brands.length === 0 && (
            <Flex direction="column" gap="4">
              <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }}/>
              {/* Brands header */}
              <Text size="3" weight="medium">Brands</Text>
              <Flex direction="column" gap="5" style={{ alignItems: "center", justifyContent: "center", width: "100%", 
                borderRadius: "var(--radius-4)", border: "1px dashed var(--gray-6)", minHeight: "200px" }}>
                <Flex direction="column" gap="2" style={{ alignItems: "center", maxWidth: "296px" }}>
                  <Text size="3" weight="medium">No brands</Text>
                  <Text size="2" color="gray" align="center">
                    Create brands to easily manage captive portals and networks across your locations.
                  </Text>
                </Flex>
                <Button size="2" variant="soft" onClick={navigateToOrganisationLocationsWithBrandModal}>
                  Manage brands
                </Button>
              </Flex>
            </Flex>
          )
        )}
      </Flex>
    </Flex>
  );
};

export default GuestPortalSelector;
